/* import __COLOCATED_TEMPLATE__ from './history-card.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { TrackedArray } from 'tracked-built-ins';
import dayjs from 'dayjs';

export default class HistoryCardComponent extends Component
{
	@service store;

	@tracked startTime = dayjs().subtract(24, 'hours').valueOf();
	@tracked endTime = dayjs().valueOf();
	@tracked activatorId = null;
	@tracked recordType = 'all'; // 'all', 'passing', 'keypad'
	@tracked showAllTime = false;
	@tracked passings = [];
	@tracked keypadEntries = [];
	@tracked combinedRecords = new TrackedArray([]);
	@tracked translations = [];
	@tracked isLoading = false;

	@action
	async fetchHistory()
	{
		this.isLoading = true;

		try {
			const filter = {};

			// Only apply time filters if showAllTime is false
			if (!this.showAllTime) {
				if (this.startTime) {
					if(filter['time'] == undefined)
						filter['time'] = {}
					filter['time']['gte'] = this.startTime;
				}
				if (this.endTime) {
					if(filter['time'] == undefined)
						filter['time'] = {}
					filter['time']['lte'] = this.endTime;
				}
			}

			// Load activators and translations FIRST to ensure they're in the store
			// This way, when passings reference activators, they'll resolve immediately
			await Promise.all([
				this.store.findAll('activator'),
				this.store.findAll('translation')
			]);
			this.translations = this.store.peekAll('translation');

			// Fetch passings - first get the list via query
			const passingFilter = { ...filter };
			if (this.activatorId) {
				passingFilter.ActivatorId = this.activatorId;
			}
			const passingQueryResults = await this.store.query('passing', { filter: passingFilter });

			// Now fetch each passing individually to get the relationship data
			// The backend includes relationships on individual record fetches
			const passingIds = passingQueryResults.map(p => p.id);
			const passingPromises = passingIds.map(id => this.store.findRecord('passing', id));
			const passings = await Promise.all(passingPromises);

			// WORKAROUND: ember-data needs time to process sideloaded relationship data
			// Even though we await the findRecord promises and preload activators,
			// ember-data performs additional async processing to link relationships.
			// This timeout ensures the relationships are fully resolved before we
			// try to access them. Without this, activator names appear empty on first load.
			await new Promise(resolve => setTimeout(resolve, 100));

			this.passings = passings;

			// Fetch keypad entries
			const keypadEntries = await this.store.query('keypad-entry', { filter });
			this.keypadEntries = [...keypadEntries];

			// Combine and sort by time
			const combined = await this.combineAndSortRecords();
			this.combinedRecords.splice(0, this.combinedRecords.length, ...combined);

		} catch (error) {
			console.error('Error fetching history:', error);
			this.passings = [];
			this.keypadEntries = [];
			this.combinedRecords.splice(0, this.combinedRecords.length);
		} finally {
			this.isLoading = false;
		}
	}

	async combineAndSortRecords()
	{
		// Await all activator relationships in parallel
		const passingRecordsPromises = this.passings.map(async (passing) => {
			const activator = await passing.ActivatorId;
			return {
				type: 'passing',
				time: passing.time,
				chip: passing.chip,
				bib: null,
				activator: activator ? activator.name : '',
				device: null,
				record: passing
			};
		});

		const passingRecords = await Promise.all(passingRecordsPromises);

		const keypadRecords = this.keypadEntries.map(entry => ({
			type: 'keypad',
			time: entry.time,
			chip: null,
			bib: entry.bib,
			activator: null,
			device: entry.deviceName,
			record: entry
		}));

		const combined = [...passingRecords, ...keypadRecords];

		// Sort by time descending (newest first)
		combined.sort((a, b) => {
			const timeA = dayjs(a.time).valueOf();
			const timeB = dayjs(b.time).valueOf();
			return timeB - timeA;
		});

		return combined;
	}

	getActivatorOrDeviceName = (record) => {
		if (record.type === 'passing') {
			return record.activator || '';
		} else if (record.type === 'keypad') {
			return record.device || '';
		}
		return '';
	}
	
	@action
	getTranslationForChip(chip)
	{
		if(!this.translations)
			return ""
		return this.translations.find(translation => translation.chip === chip);
	}

	@action
	updateStartTime(event)
	{
		const value = event.target.value;
		this.startTime = value ? dayjs(value).valueOf() : null;
	}

	@action
	updateEndTime(event)
	{
		const value = event.target.value;
		this.endTime = value ? dayjs(value).valueOf() : null;
	}

	@action
	updateActivator(event)
	{
		const value = event.target.value;
		this.activatorId = value || null;
	}

	@action
	updateRecordType(event)
	{
		const value = event.target.value;
		this.recordType = value || 'all';
	}

	@action
	toggleShowAllTime(event)
	{
		this.showAllTime = event.target.checked;
	}

	@action
	resetFilters()
	{
		this.startTime = dayjs().subtract(24, 'hours').valueOf();
		this.endTime = dayjs().valueOf();
		this.activatorId = null;
		this.recordType = 'all';
		this.showAllTime = false;
		this.fetchHistory();
	}

	get startTimeFormatted()
	{
		return this.startTime ? dayjs(this.startTime).format('YYYY-MM-DDTHH:mm') : '';
	}

	get endTimeFormatted()
	{
		return this.endTime ? dayjs(this.endTime).format('YYYY-MM-DDTHH:mm') : '';
	}

	get filteredRecords()
	{
		if (this.recordType === 'all') {
			return this.combinedRecords;
		}
		return this.combinedRecords.filter(record => record.type === this.recordType);
	}
}
